import { ApiQueryParams, DefaultQueryParams } from '@frontend/api-utils';
import { DefaultSecondaryButton } from '@frontend/components/common';
import { Application } from '@frontend/functionality/types';
import { getApplicationDisplayName } from '@frontend/functionality/utils';
import { CommonMessage } from '@frontend/lang';
import { useAccountRepository } from '@frontend/repository';
import { StockExtendedClient } from '@frontend/stock/api';
import { ExtendedProductStock } from '@frontend/stock/types';
import { useCallback } from 'react';

import { KioskList } from '../../components/kiosk-list.component';
import { KioskNavigationFooter } from '../../components/kiosk-navigation-footer.component';
import { KioskNavigationHeader } from '../../components/kiosk-navigation-header.component';
import { StockItemComponent } from '../../components/stock-item.component';
import { useAppSelector } from '../../redux/store';
import { useKioskNavigate } from '../../utils/kiosk-navigation';

const VendingStockList = () => {
    const navigate = useKioskNavigate();
    const { currentAccount } = useAccountRepository();
    const navigationState = useAppSelector((state) => state.navigation);
    const spotId = navigationState.spot?.id;

    const fetchStock = useCallback(
        async (queryParams?: ApiQueryParams<DefaultQueryParams>) => {
            if (!currentAccount || !spotId) return { results: [], count: 0 };
            const res = await StockExtendedClient.fetchConsumableProductStocksExtended({
                ...queryParams,
                account_id: currentAccount,
                spot_id: spotId
            });
            return { ...res, results: res.results.map((r) => ({ ...r, id: r.product_id })) };
        },
        [currentAccount, spotId]
    );

    type StockListItem = ExtendedProductStock & { id: string };

    const renderStockRow = (item: StockListItem, index: number, isLast: boolean) => (
        <StockItemComponent
            key={item.id}
            product={{
                id: item.product?.entity_id ?? item.product_id,
                name: item.product?.data?.name ?? item.product?.name ?? CommonMessage.ERRORS.UNKNOWN_ENTITY(CommonMessage.OBJECTS.PRODUCT.DEFAULT)
            }}
            attributes={item.product?.data?.attributes}
            quantity={item.quantity}
            isLast={isLast}
            allowNavigate
        />
    );

    return (
        <div className='bg-background-default flex size-full flex-col items-center justify-start overflow-hidden'>
            <KioskNavigationHeader
                title={CommonMessage.DETAIL.MANAGE(CommonMessage.OBJECTS.SPOT.DISTRI_SPOT)}
                onBack={() => navigate(-1)}
            />
            <div className='flex size-full flex-col items-start justify-start gap-3 overflow-y-auto px-6 py-4'>
                <p className='text-foreground-default w-full text-base font-medium'>
                    {getApplicationDisplayName(Application.VENDING)}: {CommonMessage.OBJECTS.STOCK.DEFAULT}
                </p>
                <KioskList<StockListItem>
                    id='vending-stock'
                    fetch={fetchStock}
                    rowElement={renderStockRow}
                    emptyText={CommonMessage.ERRORS.ENTITIES_NOT_FOUND(<span className='lowercase'>{CommonMessage.OBJECTS.STOCK.DEFAULT}</span>)}
                />
            </div>
            <KioskNavigationFooter
                leftButton={
                    <DefaultSecondaryButton
                        text={CommonMessage.BUTTONS.GO_BACK}
                        onClick={() => navigate(-1)}
                    />
                }
            />
        </div>
    );
};

export default VendingStockList;
